import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Camera, Loader2, User } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfilePhotoUploader = ({ currentUrl, onUploaded, label = 'Profile Photo', testid = 'profile-photo-uploader' }) => {
  const [uploading, setUploading] = useState(false);
  const [url, setUrl] = useState(currentUrl || '');
  const inputRef = useRef(null);

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5 MB');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/uploads/profile-photo`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUrl(data.url);
      onUploaded?.(data.url);
      toast.success('Photo updated');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Upload failed';
      toast.error(typeof detail === 'string' ? detail : 'Upload failed');
    } finally { setUploading(false); }
  };

  return (
    <div className="flex items-center space-x-4" data-testid={testid}>
      <div className="relative w-20 h-20 rounded-full bg-muted overflow-hidden border-2 border-border flex-shrink-0">
        {url ? (
          <img src={url} alt="profile" className="w-full h-full object-cover" data-testid={`${testid}-img`} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <User className="h-8 w-8" />
          </div>
        )}
        {uploading && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <Loader2 className="h-5 w-5 text-white animate-spin" />
          </div>
        )}
      </div>
      <div>
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={handleChange} data-testid={`${testid}-input`} />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="px-3 py-1.5 rounded border border-input text-sm inline-flex items-center hover:bg-muted disabled:opacity-50"
          data-testid={`${testid}-btn`}
        >
          <Camera className="h-3 w-3 mr-1.5" /> {url ? 'Change' : 'Upload'} {label}
        </button>
        <p className="text-xs text-muted-foreground mt-1">JPG / PNG / WEBP · Max 5 MB</p>
      </div>
    </div>
  );
};

export default ProfilePhotoUploader;
